import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

/**
 * Single source of truth for navigation entries and their permission gates.
 *
 * Consumed by:
 *   - TopBar (renders the nav items the user can see)
 *   - PrivateRoute (gates route access based on `requires`)
 *   - WelcomePage (renders the quick-launch cards filtered by `requires`)
 *
 * Each entry has:
 *   path     — URL the navigation entry routes to
 *   label    — human-readable name shown in nav UI
 *   icon     — MUI icon component for rendering
 *   requires — { section, action } passed to hasPermission().
 *              isAdmin (superadmin / company_admin) bypasses every gate.
 *
 * Permission shapes here MUST match the backend's middleware/permissions
 * matrix — drift between FE and BE in this matrix is exactly the
 * silent-403 / silent-200 trap the audit warned about.
 */
export const NAV_ITEMS = [
  {
    path: '/dashboard',
    label: 'Tableau de bord',
    icon: SpaceDashboardOutlinedIcon,
    requires: { section: 'production', action: 'view' },
  },
  {
    path: '/production',
    label: 'Production',
    icon: FactoryOutlinedIcon,
    requires: { section: 'production', action: 'view' },
  },
  {
    path: '/gmao',
    label: 'GMAO',
    icon: BuildOutlinedIcon,
    requires: { section: 'gmao', action: 'view' },
  },
  {
    path: '/audit',
    label: 'Audit',
    icon: ReceiptLongOutlinedIcon,
    requires: { section: 'audit', action: 'view' },
  },
  {
    path: '/settings',
    label: 'Réglages',
    icon: SettingsOutlinedIcon,
    requires: { section: 'settings', action: 'view' },
  },
];

/**
 * Pure helper used by PrivateRoute and the nav-item filter. Returns true
 * when the caller can access a `{ section, action }` slot. isAdmin
 * (company_admin or superadmin) bypasses every gate.
 */
export function canAccess({ requires, isAdmin, hasPermission }) {
  if (!requires) return true;
  if (isAdmin) return true;
  if (typeof hasPermission !== 'function') return false;
  return hasPermission(requires.section, requires.action);
}
